/**
 * Centralised React Query key factory for the tenant config-versioning feature.
 *
 * Both the dedicated hooks in this directory and the two integrated `DataList`
 * server-mode tables share their invalidation targets from here so a mutation
 * and the query it must freshen can never drift.
 *
 * The two list tables are fetched by `DataList`'s built-in `useFetchListData`,
 * whose query key is `['data-list', <config.id>, <serialisedParams>]`. We expose
 * the `config.id` prefixes (`historyList` / `snapshotList`) so a mutation can
 * invalidate every page/filter variant of a list with a single partial-match
 * `invalidateQueries` call.
 */

/** Stable `DataListConfig.id` values — also the DataList query-key prefixes. */
export const CONFIG_HISTORY_LIST_ID = 'claimflow-config-history';
export const CONFIG_SNAPSHOT_LIST_ID = 'claimflow-config-snapshots';

export const configVersioningKeys = {
  /** Root slug shared by every config-versioning query. */
  all: ['claimflow', 'config-versioning'] as const,

  /** A single version's detail (snapshot + diff). */
  version: (versionId: string) =>
    [...configVersioningKeys.all, 'version', versionId] as const,

  /**
   * Partial-match prefix for the history `DataList` (every page + entity_type
   * filter variant). Invalidate this after a revert so the new version row
   * appears immediately.
   */
  historyList: () => ['data-list', CONFIG_HISTORY_LIST_ID] as const,

  /**
   * Partial-match prefix for the snapshots `DataList`. Invalidate this after
   * create/restore so the list reflects the new / pre-restore snapshot.
   */
  snapshotList: () => ['data-list', CONFIG_SNAPSHOT_LIST_ID] as const,
};
